/**
 * Mutation input validation.
 *
 * Shape checks plus the critical safety rule: an unfiltered delete/update
 * (no `filter`) is rejected unless `allowUnfiltered: true` is set, to prevent
 * accidental full-table/index wipes.
 */
import { QueryBuilderError } from '../errors/query-builder-error';
import type { MutationInput, UpdateAction } from './input-types';
import { validateFilterPaths } from './validate-input';

const FIELD_TYPES = new Set(['string', 'number', 'boolean', 'date']);

export function validateMutationInput(
    input: MutationInput,
): QueryBuilderError[] {
    const errors: QueryBuilderError[] = [];

    if (!input || typeof input !== 'object') {
        return [
            new QueryBuilderError({
                code: 'INVALID_MUTATION',
                path: '$',
                message: 'Mutation input must be an object.',
            }),
        ];
    }

    if (
        input.operation !== 'delete' &&
        input.operation !== 'update' &&
        input.operation !== 'insert'
    ) {
        errors.push(
            new QueryBuilderError({
                code: 'INVALID_MUTATION',
                path: '$.operation',
                message: "operation must be 'insert', 'delete' or 'update'.",
            }),
        );
    }

    if (!input.source || typeof input.source.name !== 'string') {
        errors.push(
            new QueryBuilderError({
                code: 'INVALID_MUTATION',
                path: '$.source.name',
                message: 'source.name is required and must be a string.',
            }),
        );
    } else if (input.source.name.includes(';')) {
        errors.push(
            new QueryBuilderError({
                code: 'INVALID_IDENTIFIER',
                path: '$.source.name',
                message: 'Identifier cannot contain semicolon.',
            }),
        );
    }

    if (input.source && input.source.schema !== undefined) {
        if (typeof input.source.schema !== 'string') {
            errors.push(
                new QueryBuilderError({
                    code: 'INVALID_MUTATION',
                    path: '$.source.schema',
                    message: 'source.schema must be a string when provided.',
                }),
            );
        } else if (input.source.schema.includes(';')) {
            errors.push(
                new QueryBuilderError({
                    code: 'INVALID_IDENTIFIER',
                    path: '$.source.schema',
                    message: 'Identifier cannot contain semicolon.',
                }),
            );
        }
    }

    if (input.operation === 'insert') {
        validateInsertRows(input.rows, errors);
        validateOnConflict(input.onConflict, input.rows, errors);
        return errors;
    }

    if (input.operation === 'update') {
        const setCount = input.set?.length ?? 0;
        const actionCount = input.actions?.length ?? 0;
        if (setCount === 0 && actionCount === 0) {
            errors.push(
                new QueryBuilderError({
                    code: 'INVALID_MUTATION',
                    path: '$.set',
                    message:
                        'update requires a non-empty set list or actions list.',
                }),
            );
        }
        (input.set ?? []).forEach((assignment, i) => {
            if (!assignment.field || typeof assignment.field !== 'string') {
                errors.push(
                    new QueryBuilderError({
                        code: 'INVALID_MUTATION',
                        path: `$.set[${i}].field`,
                        message: 'set field is required.',
                    }),
                );
            }
            if (!FIELD_TYPES.has(assignment.type)) {
                errors.push(
                    new QueryBuilderError({
                        code: 'INVALID_FIELD_TYPE',
                        path: `$.set[${i}].type`,
                        message: `Invalid field type: "${assignment.type}".`,
                    }),
                );
            }
        });
        (input.actions ?? []).forEach((action, i) =>
            validateUpdateAction(action, `$.actions[${i}]`, errors),
        );
    }

    // Safety: refuse to affect all rows/documents unless explicitly allowed.
    if (!input.filter && !input.allowUnfiltered) {
        errors.push(
            new QueryBuilderError({
                code: 'UNFILTERED_MUTATION',
                path: '$.filter',
                message:
                    `${input.operation} without a filter affects ALL rows/documents. ` +
                    'Provide a filter, or set allowUnfiltered: true to confirm.',
            }),
        );
    }

    if (input.filter) {
        validateFilterPaths(input.filter, '$.filter', errors);
    }

    return errors;
}

/** Validate `insert` rows: non-empty, object rows with a consistent key set. */
function validateInsertRows(
    rows: Array<Record<string, unknown>>,
    errors: QueryBuilderError[],
): void {
    if (!Array.isArray(rows) || rows.length === 0) {
        errors.push(
            new QueryBuilderError({
                code: 'INVALID_MUTATION',
                path: '$.rows',
                message: 'insert requires a non-empty rows array.',
            }),
        );
        return;
    }

    const isPlainObject = (r: unknown): r is Record<string, unknown> =>
        !!r && typeof r === 'object' && !Array.isArray(r);

    const firstKeys = isPlainObject(rows[0]) ? Object.keys(rows[0]) : [];
    const keySignature = [...firstKeys].sort().join('\u0000');

    rows.forEach((row, i) => {
        if (!isPlainObject(row) || Object.keys(row).length === 0) {
            errors.push(
                new QueryBuilderError({
                    code: 'INVALID_MUTATION',
                    path: `$.rows[${i}]`,
                    message: 'each insert row must be a non-empty object.',
                }),
            );
            return;
        }
        if ([...Object.keys(row)].sort().join('\u0000') !== keySignature) {
            errors.push(
                new QueryBuilderError({
                    code: 'INVALID_MUTATION',
                    path: `$.rows[${i}]`,
                    message: 'all insert rows must share the same columns.',
                }),
            );
        }
    });
}

/** Validate `onConflict` upsert keys: non-empty strings that exist as columns. */
function validateOnConflict(
    onConflict: string[] | undefined,
    rows: Array<Record<string, unknown>>,
    errors: QueryBuilderError[],
): void {
    if (onConflict === undefined) {
        return;
    }
    if (!Array.isArray(onConflict) || onConflict.length === 0) {
        errors.push(
            new QueryBuilderError({
                code: 'INVALID_MUTATION',
                path: '$.onConflict',
                message:
                    'onConflict must be a non-empty array of column names.',
            }),
        );
        return;
    }
    const columns = new Set(
        rows[0] && typeof rows[0] === 'object' ? Object.keys(rows[0]) : [],
    );
    onConflict.forEach((key, i) => {
        if (typeof key !== 'string' || key.length === 0) {
            errors.push(
                new QueryBuilderError({
                    code: 'INVALID_MUTATION',
                    path: `$.onConflict[${i}]`,
                    message: 'onConflict keys must be non-empty strings.',
                }),
            );
        } else if (!columns.has(key)) {
            errors.push(
                new QueryBuilderError({
                    code: 'INVALID_MUTATION',
                    path: `$.onConflict[${i}]`,
                    message: `onConflict key "${key}" is not one of the inserted columns.`,
                }),
            );
        }
    });
}

/** Validate a granular update action (increment/append/removeField/set). */
function validateUpdateAction(
    action: UpdateAction,
    path: string,
    errors: QueryBuilderError[],
): void {
    if (!action || typeof action !== 'object') {
        errors.push(
            new QueryBuilderError({
                code: 'INVALID_MUTATION',
                path,
                message: 'update action must be an object.',
            }),
        );
        return;
    }
    if (!action.field || typeof action.field !== 'string') {
        errors.push(
            new QueryBuilderError({
                code: 'INVALID_MUTATION',
                path: `${path}.field`,
                message: 'update action field is required.',
            }),
        );
    }
    if (action.op === 'increment' && typeof action.by !== 'number') {
        errors.push(
            new QueryBuilderError({
                code: 'INVALID_MUTATION',
                path: `${path}.by`,
                message: 'increment "by" must be a number.',
            }),
        );
    }
    if (
        (action.op === 'set' || action.op === 'append') &&
        !FIELD_TYPES.has(action.type)
    ) {
        errors.push(
            new QueryBuilderError({
                code: 'INVALID_FIELD_TYPE',
                path: `${path}.type`,
                message: `Invalid field type: "${action.type}".`,
            }),
        );
    }
    if (
        action.op !== 'set' &&
        action.op !== 'increment' &&
        action.op !== 'append' &&
        action.op !== 'removeField'
    ) {
        errors.push(
            new QueryBuilderError({
                code: 'INVALID_MUTATION',
                path: `${path}.op`,
                message: `Unknown update action op: "${
                    (action as { op: string }).op
                }".`,
            }),
        );
    }
}
